import type { Client, Contact } from '../types/crm';
import { clientById, contactLabel, idsMatch } from './crmHelpers';
import { contactSourceAccountName } from './accountHierarchy';
import { formatSalespersonCode } from './salespersonScope';

export interface ContactAccountDisplayGroup {
  parent: Client;
  brands: Client[];
}

export interface DedupedAccountContact {
  contact: Contact;
  brandNames: string[];
  relatedContacts: Contact[];
}

export interface DedupedContactListEntry {
  contact: Contact;
  accounts: Client[];
  relatedContacts: Contact[];
}

function normalizeEmail(email?: string): string {
  return email?.trim().toLowerCase() ?? '';
}

function normalizePhone(phone?: string): string {
  return (phone ?? '').replace(/\D/g, '');
}

/** Stable identity for the same person across multiple account records. */
export function buildContactDedupKey(contact: Contact): string {
  const email = normalizeEmail(contact.email);
  if (email) return `email:${email}`;

  const phone = normalizePhone(contact.phone);
  if (phone) return `phone:${phone}`;

  const first = contact.first_name?.trim().toLowerCase() ?? '';
  const last = contact.last_name?.trim().toLowerCase() ?? '';
  return `name:${first}|${last}`;
}

function contactCompletenessScore(contact: Contact): number {
  let score = 0;
  if (contact.email?.trim()) score += 4;
  if (contact.phone?.trim()) score += 2;
  if (contact.job_title?.trim()) score += 1;
  if (contact.created_at) score += 0.1;
  return score;
}

/** Group linked accounts by mother company for Contacts list display. */
export function groupContactAccountsForDisplay(
  accounts: Client[],
  clients: Client[],
): ContactAccountDisplayGroup[] {
  const byParent = new Map<string, ContactAccountDisplayGroup>();

  for (const account of accounts) {
    if (account.parent_client_id) {
      const parent = clientById(clients, account.parent_client_id) ?? account;
      const key = String(parent.id);
      const entry = byParent.get(key) ?? { parent, brands: [] };
      entry.brands.push(account);
      byParent.set(key, entry);
      continue;
    }

    const key = String(account.id);
    const existing = byParent.get(key);
    if (existing) {
      existing.parent = account;
    } else {
      byParent.set(key, { parent: account, brands: [] });
    }
  }

  return [...byParent.values()]
    .map(({ parent, brands }) => ({
      parent,
      brands: [...brands].sort((a, b) => a.company_name.localeCompare(b.company_name)),
    }))
    .sort((a, b) => a.parent.company_name.localeCompare(b.parent.company_name));
}

export function pickPreferredContact(contacts: Contact[]): Contact {
  return [...contacts].sort((a, b) => {
    const scoreDiff = contactCompletenessScore(b) - contactCompletenessScore(a);
    if (scoreDiff !== 0) return scoreDiff;
    return (b.created_at ?? '').localeCompare(a.created_at ?? '');
  })[0]!;
}

export function dedupeContactsForAccountView(
  contacts: Contact[],
  clients: Client[],
  viewingClient: Client,
): DedupedAccountContact[] {
  const groups = new Map<string, { contacts: Contact[]; brands: Set<string> }>();

  for (const contact of contacts) {
    const key = buildContactDedupKey(contact);
    const group = groups.get(key) ?? { contacts: [], brands: new Set<string>() };
    group.contacts.push(contact);

    const brand = contactSourceAccountName(clients, contact, viewingClient);
    if (brand) group.brands.add(brand);

    groups.set(key, group);
  }

  return [...groups.values()]
    .map(({ contacts: groupedContacts, brands }) => ({
      contact: pickPreferredContact(groupedContacts),
      brandNames: [...brands].sort((a, b) => a.localeCompare(b)),
      relatedContacts: groupedContacts,
    }))
    .sort((a, b) => contactLabel(a.contact).localeCompare(contactLabel(b.contact)));
}

export function dedupeContactsForList(contacts: Contact[], clients: Client[]): DedupedContactListEntry[] {
  const groups = new Map<string, { contacts: Contact[]; accounts: Client[] }>();

  for (const contact of contacts) {
    const key = buildContactDedupKey(contact);
    const group = groups.get(key) ?? { contacts: [], accounts: [] };
    group.contacts.push(contact);

    const account = clientById(clients, contact.client_id);
    if (account && !group.accounts.some((row) => idsMatch(row.id, account.id))) {
      group.accounts.push(account);
    }

    groups.set(key, group);
  }

  return [...groups.values()]
    .map(({ contacts: groupedContacts, accounts }) => ({
      contact: pickPreferredContact(groupedContacts),
      accounts: [...accounts].sort((a, b) => a.company_name.localeCompare(b.company_name)),
      relatedContacts: groupedContacts,
    }))
    .sort((a, b) => contactLabel(a.contact).localeCompare(contactLabel(b.contact)));
}

export function contactSearchPool(
  entry: DedupedContactListEntry,
  salespersonCode: string,
  options?: { includeSalesperson?: boolean },
): string {
  const includeSalesperson = options?.includeSalesperson !== false;
  const accountNames = entry.accounts.map((account) => `${account.company_name} ${account.code}`).join(' ');
  const accountSalespeople = includeSalesperson
    ? entry.accounts
        .map((account) => (account.owner_id ?? '').trim())
        .filter(Boolean)
        .join(' ')
    : '';
  const contact = entry.contact;
  const spPart = includeSalesperson ? `${salespersonCode} ${accountSalespeople}` : '';
  return `${contactLabel(contact)} ${contact.email ?? ''} ${contact.phone ?? ''} ${accountNames} ${spPart}`.toLowerCase();
}

/** Unique salesperson codes from linked accounts (clients.owner_id). */
export function salespersonCodesForContactAccounts(accounts: Client[]): string {
  const codes = [
    ...new Set(
      accounts
        .map((account) => formatSalespersonCode(account.owner_id ?? ''))
        .filter(Boolean),
    ),
  ];
  return codes.length > 0 ? codes.join(' · ') : '—';
}
